import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useAuthStore } from '../../../auth/state';
import { FaList, FaCalendarAlt, FaEdit, FaTrash, FaExternalLinkAlt, FaSpinner, FaCrown, FaTimes, FaUsers, FaPlus, FaClock, FaHandshake, FaVideo } from 'react-icons/fa';
import './adm_gen_meet.css';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import CreateMeetingModal from '../components/CreateMeetingModal';
import EditMeetingModal from '../components/EditMeetingModal';
import { db, auth } from '../../../auth/firebase';
import { doc, getDoc, updateDoc, deleteField, arrayUnion, arrayRemove } from 'firebase/firestore';
import { collection, getDocs } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';

function AdminGeneralMeetings() {
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const [activeTab, setActiveTab] = useState(0);
  const [meetings, setMeetings] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [calendarKey, setCalendarKey] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInvitedList, setShowInvitedList] = useState(null);
  const [hostData, setHostData] = useState({});
  const [showParticipantsPopup, setShowParticipantsPopup] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // # Tab configuration
  const tabs = [
    {
      label: 'Meeting List',
      icon: <FaList className="adm_gen_meet_tab_icon" />,
    },
    {
      label: 'Calendar View',
      icon: <FaCalendarAlt className="adm_gen_meet_tab_icon" />,
    }
  ];

  // # Filter meetings based on search
  const filteredMeetings = meetings.filter(meeting => {
    const searchLower = searchQuery.toLowerCase();
    return (
      meeting.title.toLowerCase().includes(searchLower) ||
      meeting.description.toLowerCase().includes(searchLower) ||
      hostData[meeting.host.uid]?.displayName?.toLowerCase().includes(searchLower) ||
      hostData[meeting.host.uid]?.email?.toLowerCase().includes(searchLower)
    );
  });

  // # Fetch user data (hosts and participants)
  const fetchUserData = async (meetings) => {
    // # Get unique user IDs from both hosts and invited participants
    const uniqueUserIds = [...new Set(meetings.flatMap(meeting => 
      [meeting.host.uid, ...meeting.invited]
    ))];

    const userDataPromises = uniqueUserIds.map(async (uid) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          return [uid, {
            displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.username || 'Unknown',
            email: data.email || 'No email',
            avatar: data.photoURL || null,
            username: data.username || null
          }];
        }
      } catch (error) {
        console.error(`Error fetching user data for ${uid}:`, error);
      }
      return [uid, { displayName: 'Unknown User', email: 'No email', avatar: null }];
    });

    const userDataResults = await Promise.all(userDataPromises);
    setHostData(Object.fromEntries(userDataResults));
  };

  // # Fetch meetings with user data
  const fetchMeetings = async () => {
    try {
      const genMeetingsRef = doc(db, 'meetings', 'gen_meetings');
      const docSnap = await getDoc(genMeetingsRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const meetingsArray = Object.entries(data).map(([id, meeting]) => ({
          id,
          ...meeting,
          meet_time: meeting.meet_time instanceof Date 
            ? meeting.meet_time 
            : typeof meeting.meet_time === 'string' 
              ? new Date(meeting.meet_time) 
              : meeting.meet_time.toDate()
        }));
        meetingsArray.sort((a, b) => b.meet_time - a.meet_time);
        setMeetings(meetingsArray);
        
        // # Fetch data for all users (hosts and participants)
        await fetchUserData(meetingsArray);
      } else {
        setMeetings([]);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  };

  // # Initial meetings fetch
  useEffect(() => {
    fetchMeetings();
  }, []);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Handle meeting deletion
  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      const meetingsRef = doc(db, 'meetings', 'gen_meetings');
      
      // # Delete the meeting using deleteField
      await updateDoc(meetingsRef, {
        [meetingId]: deleteField()
      });

      // # Refresh meetings list
      fetchMeetings();
      setDeleteError('');
    } catch (error) {
      console.error('Error deleting meeting:', error);
      setDeleteError('Failed to delete meeting. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // # Handle meeting edit
  const handleEditMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setShowEditModal(true);
  };

  // # Handle modal close
  const handleCreateModalClose = () => {
    setShowCreateModal(false);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedMeeting(null);
  };

  // # Handle successful meeting creation/edit
  const handleMeetingCreated = () => {
    fetchMeetings();
    handleCreateModalClose();
  };

  const handleMeetingEdited = () => {
    fetchMeetings();
    handleEditModalClose();
  };

  // # Handle calendar event click
  const handleEventClick = (eventInfo) => {
    const meeting = meetings.find(m => m.id === eventInfo.event.id);
    if (meeting) {
      setSelectedMeeting(meeting);
      setShowEditModal(true);
    }
  };

  // # Format date and time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit'
      })
    };
  };

  // # Participants Popup Component
  const ParticipantsPopup = ({ meeting, onClose }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [availableUsers, setAvailableUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // # Fetch available users on component mount
    useEffect(() => {
      fetchAvailableUsers();
    }, []);

    // # Fetch users that can be added to the meeting
    const fetchAvailableUsers = async () => {
      setLoading(true);
      setError('');
      try {
        const usersRef = collection(db, 'users');
        const usersSnap = await getDocs(usersRef);
        const users = usersSnap.docs
          .map(doc => ({ uid: doc.id, ...doc.data() }))
          .filter(user => !meeting.invited.includes(user.uid));
        setAvailableUsers(users);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load available users');
      } finally {
        setLoading(false);
      }
    };

    // # Add participant to meeting
    const handleAddParticipant = async (userId) => {
      setLoading(true);
      setError('');
      try {
        const meetingsRef = doc(db, 'meetings', 'gen_meetings');
        await updateDoc(meetingsRef, {
          [`${meeting.id}.invited`]: arrayUnion(userId)
        });
        
        // # Update local state immediately
        meeting.invited.push(userId);
        setShowParticipantsPopup({ ...meeting });
        
        // # Refresh meetings list and available users
        fetchMeetings();
        fetchAvailableUsers();
      } catch (err) {
        console.error('Error adding participant:', err);
        setError('Failed to add participant');
      } finally {
        setLoading(false);
      }
    };

    // # Remove participant from meeting
    const handleRemoveParticipant = async (userId) => {
      if (userId === meeting.host.uid) {
        setError("Cannot remove the host from the meeting");
        return;
      }

      if (!window.confirm('Are you sure you want to remove this participant?')) {
        return;
      }

      setLoading(true);
      setError('');
      try {
        const meetingsRef = doc(db, 'meetings', 'gen_meetings');
        await updateDoc(meetingsRef, {
          [`${meeting.id}.invited`]: arrayRemove(userId)
        });

        // # Update local state immediately
        meeting.invited = meeting.invited.filter(id => id !== userId);
        setShowParticipantsPopup({ ...meeting });
        
        // # Refresh meetings list and available users
        fetchMeetings();
        fetchAvailableUsers();
      } catch (err) {
        console.error('Error removing participant:', err);
        setError('Failed to remove participant');
      } finally {
        setLoading(false);
      }
    };

    // # Filter available users based on search
    const filteredAvailableUsers = availableUsers.filter(user => {
      const searchLower = searchQuery.toLowerCase();
      return (
        user.firstName?.toLowerCase().includes(searchLower) ||
        user.lastName?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.username?.toLowerCase().includes(searchLower)
      );
    });

    if (!meeting) return null;

    return (
      <>
        <div className="adm_gen_meet_participants_popup_overlay" onClick={onClose} />
        <div className="adm_gen_meet_participants_popup">
          <div className="adm_gen_meet_participants_popup_header">
            <h3 className="adm_gen_meet_participants_popup_title">
              <FaUsers /> Meeting Participants ({meeting.invited.length})
            </h3>
            <button className="adm_gen_meet_participants_popup_close" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          {error && (
            <div className="adm_gen_meet_error">
              {error}
            </div>
          )}

          <div className="adm_gen_meet_participants_search">
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="adm_gen_meet_search_input"
            />
          </div>

          <div className="adm_gen_meet_participants_sections">
            {/* Current Participants Section */}
            <div className="adm_gen_meet_participants_section">
              <h4 className="adm_gen_meet_section_title">Current Participants</h4>
              <div className="adm_gen_meet_invited_grid">
                {meeting.invited.map((uid) => {
                  const participant = hostData[uid] || {};
                  const isHost = uid === meeting.host.uid;
                  return (
                    <div key={uid} className="adm_gen_meet_invited_item">
                      {participant.avatar ? (
                        <img src={participant.avatar} alt={participant.displayName} className="adm_gen_meet_invited_avatar" />
                      ) : (
                        <div className="adm_gen_meet_invited_avatar_placeholder">
                          {participant.displayName?.charAt(0) || '?'}
                        </div>
                      )}
                      <div className="adm_gen_meet_invited_details">
                        <div className="adm_gen_meet_host_name_container">
                          <span className="adm_gen_meet_invited_name">
                            {participant.displayName}
                            {participant.username && (
                              <span className="adm_gen_meet_username">@{participant.username}</span>
                            )}
                          </span>
                          {isHost && (
                            <span className="adm_gen_meet_host_badge">
                              <FaCrown /> Host
                            </span>
                          )}
                        </div>
                        <span className="adm_gen_meet_invited_email">{participant.email}</span>
                      </div>
                      {!isHost && (
                        <button
                          className="adm_gen_meet_remove_participant_btn"
                          onClick={() => handleRemoveParticipant(uid)}
                          disabled={loading}
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Available Users Section */}
            <div className="adm_gen_meet_participants_section">
              <h4 className="adm_gen_meet_section_title">Add Participants</h4>
              <div className="adm_gen_meet_invited_grid">
                {loading ? (
                  <div className="adm_gen_meet_loading">
                    <FaSpinner className="icon-spin" /> Loading...
                  </div>
                ) : filteredAvailableUsers.length > 0 ? (
                  filteredAvailableUsers.map((user) => (
                    <div key={user.uid} className="adm_gen_meet_invited_item">
                      {user.photoURL ? (
                        <img src={user.photoURL} alt={user.firstName} className="adm_gen_meet_invited_avatar" />
                      ) : (
                        <div className="adm_gen_meet_invited_avatar_placeholder">
                          {user.firstName?.charAt(0) || '?'}
                        </div>
                      )}
                      <div className="adm_gen_meet_invited_details">
                        <span className="adm_gen_meet_invited_name">
                          {`${user.firstName} ${user.lastName}`}
                          {user.username && (
                            <span className="adm_gen_meet_username">@{user.username}</span>
                          )}
                        </span>
                        <span className="adm_gen_meet_invited_email">{user.email}</span>
                      </div>
                      <button
                        className="adm_gen_meet_add_participant_btn"
                        onClick={() => handleAddParticipant(user.uid)}
                        disabled={loading}
                      >
                        <FaPlus />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="adm_gen_meet_empty">
                    <p>No users available to add</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // # Update the events mapping to include host data
  const events = meetings.map(meeting => ({
    id: meeting.id,
    title: meeting.title,
    start: meeting.meet_time,
    end: new Date(new Date(meeting.meet_time).getTime() + 3600000),
    hostData: hostData[meeting.host.uid] || {
      displayName: 'Loading...',
      email: '',
      avatar: null,
      username: null
    },
    className: new Date(meeting.meet_time) < new Date() ? 'fc-event-past' : '',
    display: 'block'
  }));

  // # Update calendar options to include custom event rendering
  const calendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay'
    },
    events: events,
    eventClick: handleEventClick,
    editable: false,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    height: 'auto',
    aspectRatio: 1.8,
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      meridiem: 'short'
    },
    eventContent: (eventInfo) => {
      const event = eventInfo.event;
      const hostData = event.extendedProps.hostData;
      const hostName = hostData?.displayName || 'Loading...';
      
      return {
        html: `
          <div class="adm_gen_meet_calendar_event">
            <h1 class="adm_gen_meet_calendar_event_title">${event.title}</h1>
            <h2 class="adm_gen_meet_calendar_event_host">
              <span class="adm_gen_meet_calendar_host_icon">👑</span>
              ${hostName}
            </h2>
            <h3 class="adm_gen_meet_calendar_event_time">
              <span class="adm_gen_meet_calendar_time_icon">🕒</span>
              ${event.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </h3>
          </div>
        `
      };
    },
    slotMinTime: '06:00:00',
    slotMaxTime: '22:00:00',
    allDaySlot: false,
    nowIndicator: true,
    weekNumbers: true,
    weekNumberFormat: { week: 'numeric' },
    dayCellClassNames: 'adm_gen_meet_calendar_cell'
  };

  return (
    <div className={`adm_gen_meet_container ${isDarkMode ? 'adm_gen_meet_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="adm_gen_meet_content">
        <div className="adm_gen_meet_header">
          <h1>
            <FaVideo style={{ marginRight: '12px', verticalAlign: 'middle' }} />
            General Meetings Management
          </h1>
          <button 
            className="adm_gen_meet_create_btn"
            onClick={() => setShowCreateModal(true)}
          >
            <FaPlus />
            Create New Meeting
          </button>
        </div>

        {deleteError && (
          <div className="adm_gen_meet_error" role="alert">
            {deleteError}
          </div>
        )}

        <div className="adm_gen_meet_tabs">
          <Tabs
            selectedIndex={activeTab}
            onSelect={setActiveTab}
            selectedTabClassName="adm_gen_meet_tab--selected"
          >
            <TabList className="adm_gen_meet_tab_list">
              {tabs.map((tab, index) => (
                <Tab 
                  key={index}
                  className={`adm_gen_meet_tab ${activeTab === index ? 'active' : ''}`}
                >
                  {tab.icon}
                  <span className="adm_gen_meet_tab_label">
                    {tab.label}
                    {activeTab === index && (
                      <span className="adm_gen_meet_tab_active_indicator" />
                    )}
                  </span>
                </Tab>
              ))}
            </TabList>

            {/* # Meeting List Tab */}
            <TabPanel className={`adm_gen_meet_tab_panel ${activeTab === 0 ? 'active' : ''}`}>
              <div className="adm_gen_meet_list">
                <div className="adm_gen_meet_search">
                  <input
                    type="text"
                    placeholder="Search meetings by title, description, or host..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="adm_gen_meet_search_input"
                  />
                </div>
                {filteredMeetings.length > 0 ? (
                  filteredMeetings.map(meeting => {
                    const { date, time } = formatDateTime(meeting.meet_time);
                    const host = hostData[meeting.host.uid] || {};
                    return (
                      <div key={meeting.id} className="adm_gen_meet_item">
                        <div className="adm_gen_meet_item_header">
                          <h3>{meeting.title}</h3>
                          <div className="adm_gen_meet_item_actions">
                            <button 
                              className="adm_gen_meet_action_btn edit"
                              onClick={() => handleEditMeeting(meeting)}
                              title="Edit Meeting"
                              disabled={isDeleting}
                            >
                              <FaEdit />
                            </button>
                            <button 
                              className={`adm_gen_meet_action_btn delete ${isDeleting ? 'loading' : ''}`}
                              onClick={() => handleDeleteMeeting(meeting.id)}
                              title="Delete Meeting"
                              disabled={isDeleting}
                            >
                              {isDeleting ? <FaSpinner className="icon-spin" /> : <FaTrash />}
                            </button>
                            <a 
                              href={meeting.host.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="adm_gen_meet_action_btn join"
                              title="Join Meeting"
                            >
                              <FaExternalLinkAlt />
                            </a>
                          </div>
                        </div>
                        <p className="adm_gen_meet_item_desc">{meeting.description}</p>
                        <div className="adm_gen_meet_host_info">
                          {host.avatar ? (
                            <img src={host.avatar} alt={host.displayName} className="adm_gen_meet_host_avatar" />
                          ) : (
                            <div className="adm_gen_meet_host_avatar_placeholder">
                              {host.displayName?.charAt(0) || '?'}
                            </div>
                          )}
                          <div className="adm_gen_meet_host_details">
                            <div className="adm_gen_meet_host_name_container">
                              <span className="adm_gen_meet_host_name">{host.displayName}</span>
                              <span className="adm_gen_meet_host_badge">
                                <FaCrown /> Host
                              </span>
                            </div>
                            <span className="adm_gen_meet_host_email">{host.email}</span>
                          </div>
                        </div>
                        <div className="adm_gen_meet_item_details">
                          <span className="adm_gen_meet_item_date">
                            <FaCalendarAlt /> {date} at {time}
                          </span>
                          <button
                            className="adm_gen_meet_participants_btn"
                            onClick={() => setShowParticipantsPopup(meeting)}
                          >
                            <FaUsers />
                            {meeting.invited.length} Participant{meeting.invited.length !== 1 ? 's' : ''}
                          </button>
                        </div>
                        {showInvitedList === meeting.id && (
                          <div className="adm_gen_meet_invited_list">
                            <h4>Invited Participants</h4>
                            <div className="adm_gen_meet_invited_grid">
                              {meeting.invited.map((uid) => {
                                const participant = hostData[uid] || {};
                                return (
                                  <div key={uid} className="adm_gen_meet_invited_item">
                                    {participant.avatar ? (
                                      <img src={participant.avatar} alt={participant.displayName} className="adm_gen_meet_invited_avatar" />
                                    ) : (
                                      <div className="adm_gen_meet_invited_avatar_placeholder">
                                        {participant.displayName?.charAt(0) || '?'}
                                      </div>
                                    )}
                                    <div className="adm_gen_meet_invited_details">
                                      <span className="adm_gen_meet_invited_name">{participant.displayName}</span>
                                      <span className="adm_gen_meet_invited_email">{participant.email}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="adm_gen_meet_empty">
                    <FaCalendarAlt className="adm_gen_meet_empty_icon" />
                    <p>No meetings scheduled yet</p>
                    <button 
                      className="adm_gen_meet_empty_btn"
                      onClick={() => setShowCreateModal(true)}
                    >
                      Schedule a Meeting
                    </button>
                  </div>
                )}
              </div>
            </TabPanel>

            {/* # Calendar Tab */}
            <TabPanel className={`adm_gen_meet_tab_panel ${activeTab === 1 ? 'active' : ''}`}>
              <div className="adm_gen_meet_calendar">
                <FullCalendar
                  key={calendarKey}
                  {...calendarOptions}
                />
              </div>
            </TabPanel>
          </Tabs>
        </div>

        {/* # Create Meeting Modal */}
        <CreateMeetingModal
          open={showCreateModal}
          onClose={handleCreateModalClose}
          meetingType="general"
          onSuccess={handleMeetingCreated}
        />

        {/* # Edit Meeting Modal */}
        {selectedMeeting && (
          <EditMeetingModal
            open={showEditModal}
            onClose={handleEditModalClose}
            meeting={selectedMeeting}
            onSuccess={handleMeetingEdited}
          />
        )}

        {/* Add the participants popup */}
        {showParticipantsPopup && (
          <ParticipantsPopup
            meeting={showParticipantsPopup}
            onClose={() => setShowParticipantsPopup(null)}
          />
        )}
      </div>
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default AdminGeneralMeetings;
